// File: src/components/header/NotificationDropdown.tsx
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSettings } from "../../context/SettingsContext";

type Notification = {
  text: string;
  status: string;
  lead_name: string;
  email: string;
  company_name: string | null;
  company_website: string | null;
  time_ago: string;
};

type ApiResponse = {
  data?: Notification[];
  count?: number;
  status?: number;
  message?: string;
};

const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifying, setNotifying] = useState(true);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { settings } = useSettings();
  const [isDarkMode, setIsDarkMode] = useState(false);

  const token = sessionStorage.getItem("access_token");

  // Define color arrays for text and status
  const textColors = [
    "bg-blue-500",
    "bg-green-500",
    "bg-red-500",
    "bg-yellow-500",
    "bg-purple-500",
    "bg-teal-500",
    "bg-pink-500",
  ];
  const statusColors = [
    "text-blue-600 dark:text-blue-400",
    "text-green-600 dark:text-green-400",
    "text-red-600 dark:text-red-400",
    "text-yellow-600 dark:text-yellow-400",
    "text-purple-600 dark:text-purple-400",
    "text-teal-600 dark:text-teal-400",
    "text-pink-600 dark:text-pink-400",
  ];

  // Check for dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains("dark"));
    };

    checkDarkMode();

    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!token) {
      setError("Authorization required. Please log in.");
      navigate("/signin");
      return;
    }

    const fetchNotifications = async (retryCount = 0): Promise<void> => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/notification/list`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 429) {
          const retryAfter =
            response.headers.get("Retry-After") ||
            Math.pow(2, retryCount) * 1000;
          if (retryCount < 3) {
            const delay =
              typeof retryAfter === "string"
                ? parseInt(retryAfter, 10) * 1000
                : retryAfter;
            await new Promise((resolve) => setTimeout(resolve, delay));
            return fetchNotifications(retryCount + 1);
          } else {
            throw new Error("Rate limit exceeded. Please try again later.");
          }
        }

        if (!response.ok) {
          throw new Error("Failed to fetch notifications");
        }

        const result: ApiResponse = await response.json();

        if (result.status === 200 && result.message === "Leads not found") {
          setNotifications([]);
          setNotificationCount(0);
          setNotifying(false);
          return;
        }

        if (result && Array.isArray(result.data)) {
          setNotifications(result.data);
          setNotificationCount(result.count || result.data.length);
          setNotifying(result.data.length > 0);
        } else {
          throw new Error("Unexpected API response");
        }
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("An unknown error occurred");
        }
      }
    };

    fetchNotifications();
  }, [token, navigate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    setNotifying(false);
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  const displayedNotifications = showAll
    ? notifications
    : notifications.slice(0, 4);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="relative flex items-center justify-center w-12 h-12 transition-all duration-300 dark:bg-gray-900 dark:hover:bg-gray-800 rounded-full shadow-sm hover:shadow-md border border-gray-200 dark:border-gray-700 hover:bg-blue-100 dark:hover:bg-gray-700"
        style={{
          color: isDarkMode ? "#d1d5db" : settings.fontColor || "#000000",
        }}
      >
        <span
          className={`absolute top-1 right-1 h-3 w-3 rounded-full bg-gradient-to-r from-orange-400 to-orange-600 shadow-md transition-opacity duration-300 ${
            notifying ? "opacity-100" : "opacity-0"
          }`}
        >
          <span className="absolute inset-0 bg-orange-400 rounded-full opacity-75 animate-ping"></span>
        </span>
        <svg
          className="w-5 h-5 fill-current"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.75 2.29248C10.75 1.87827 10.4143 1.54248 10 1.54248C9.58583 1.54248 9.25004 1.87827 9.25004 2.29248V2.83613C6.08266 3.20733 3.62504 5.9004 3.62504 9.16748V14.4591H3.33337C2.91916 14.4591 2.58337 14.7949 2.58337 15.2091C2.58337 15.6234 2.91916 15.9591 3.33337 15.9591H4.37504H15.625H16.6667C17.0809 15.9591 17.4167 15.6234 17.4167 15.2091C17.4167 14.7949 17.0809 14.4591 16.6667 14.4591H16.375V9.16748C16.375 5.9004 13.9174 3.20733 10.75 2.83613V2.29248ZM14.875 14.4591V9.16748C14.875 6.47509 12.6924 4.29248 10 4.29248C7.30765 4.29248 5.12504 6.47509 5.12504 9.16748V14.4591H14.875ZM8.00004 17.7085C8.00004 18.1228 8.33583 18.4585 8.75004 18.4585H11.25C11.6643 18.4585 12 18.1228 12 17.7085C12 17.2943 11.6643 16.9585 11.25 16.9585H8.75004C8.33583 16.9585 8.00004 17.2943 8.00004 17.7085Z"
            fill="currentColor"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-3 w-80 sm:w-96 max-h-[500px] bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-300 transform origin-top-right">
          <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
            <h5 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              Notifications ({notificationCount})
            </h5>
            <button
              onClick={closeDropdown}
              className="p-1 transition-colors"
              style={{
                color: isDarkMode ? "#d1d5db" : settings.fontColor || "#000000",
              }}
            >
              <svg
                className="w-5 h-5 fill-current"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M6.21967 7.28131C5.92678 6.98841 5.92678 6.51354 6.21967 6.22065C6.51256 5.92775 6.98744 5.92775 7.28033 6.22065L11.999 10.9393L16.7176 6.22078C17.0105 5.92789 17.4854 5.92788 17.7782 6.22078C18.0711 6.51367 18.0711 6.98855 17.7782 7.28144L13.0597 12L17.7782 16.7186C18.0711 17.0115 18.0711 17.4863 17.7782 17.7792C17.4854 18.0721 17.0105 18.0721 16.7176 17.7792L11.999 13.0607L7.28033 17.7794C6.98744 18.0722 6.51256 18.0722 6.21967 17.7794C5.92678 17.4865 5.92678 17.0116 6.21967 16.7187L10.9384 12L6.21967 7.28131Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>

          {error ? (
            <div className="p-4 text-red-500 dark:text-red-400 text-center">
              {error}
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-gray-500 dark:text-gray-400 text-center">
              No notifications available
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700 max-h-[400px] overflow-y-auto">
              {displayedNotifications.map((notification, index) => (
                <li
                  key={index}
                  className="flex items-start gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div
                    className={`relative flex items-center justify-center w-10 h-10 rounded-full ${
                      textColors[index % textColors.length]
                    } text-white font-bold text-lg shadow-sm`}
                  >
                    <span className="w-10 h-10 flex items-center justify-center">
                      {notification.text || "?"}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      <div className="space-x-1">
                        <span className="font-semibold text-gray-800 dark:text-gray-100 whitespace-nowrap">
                          {notification.lead_name}
                        </span>
                        <span>({notification.email})</span>
                      </div>
                      <div className="mt-1">
                        <span>Status updated to </span>
                        <span
                          className={`font-semibold ${
                            statusColors[index % statusColors.length]
                          }`}
                        >
                          {notification.status}
                        </span>
                      </div>
                    </div>
                    {notification.company_name && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Company: {notification.company_name}
                      </div>
                    )}
                    {notification.company_website && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Website:{" "}
                        <a
                          href={notification.company_website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:underline"
                        >
                          {notification.company_website}
                        </a>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 mt-1">
                      <span>Notification</span>
                      <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                      <span>{notification.time_ago}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {notifications.length > 4 && !showAll && (
            <button
              onClick={() => setShowAll(true)}
              className="w-full py-3 text-sm font-medium text-blue-600 dark:text-blue-400 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            >
              View All Notifications
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
